<template>
  <div id="payment">
    <template v-if="!form.bankTransfer">
      <h3>Card Payment</h3>
      <div class="form-group">
        <p class="py-4 ml-8 mb-0">
          Your payment is processed securely through Stripe. Once your card is confirmed at checkout, a payment
          confirmation is emailed to you straight away and your order goes into production.
        </p>
        <p class="ml-8 pb-4 mb-0">
          <small><i>Prefer to pay another way? Let us know if you'd like to switch to bank transfer.</i></small>
        </p>
      </div>
    </template>
    <template v-else>
      <h3>Bank Transfer Payment</h3>
      <div class="form-group">
        <p class="py-4 ml-8 mb-0">
          When you submit this form, we'll email you Plykea's bank details — including your order number as payment
          reference — so you can make your transfer. Once you've sent it, just let your Quote contact know so we can
          match your payment and move your order into production.
        </p>
        <p class="ml-8 pb-4 mb-0">
          <small><i>Prefer to pay instantly? Let us know if you'd like to switch to card payment.</i></small>
        </p>
      </div>
    </template>
    <div v-if="!form.bankTransfer">
      <h3>Card Details</h3>
      <div class="form-group">
        <v-text-field
          v-model="stripeObjects.cardName"
          label="Name on Card"
          :rules="[$rules.required('Name on Card')]"
          required
        >
        </v-text-field>
        <v-row>
          <v-col cols="11">
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardNumberFocussed }">Card Number</div>
              <div ref="cardNumber"></div>
            </div>
          </v-col>
          <v-col cols="1" class="mt-2">
            <transition name="fade">
              <img :src="cardIcons[cardType]" v-if="cardIcons[cardType]" alt="Payment Card Icon" height="20" class="" />
            </transition>
          </v-col>
          <v-col cols="12" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardNumber }}
            </div></v-col
          >
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardExpiryFocussed }">Expiration Date (MM/YY)</div>
              <div ref="cardExpiry"></div>
            </div>
          </v-col>
          <v-col cols="10" md="5">
            <div class="stripe-input">
              <div class="fancy-label" :class="{ tiny: cardCvcFocussed }">Security Code</div>
              <div ref="cardCvc"></div>
            </div>
          </v-col>
          <v-col cols="2" md="1" class="mt-2">
            <transition name="fade">
              <img :src="cardIcons.security" v-if="cardIcons.security" alt="Payment Card Icon" height="20" />
            </transition>
          </v-col>
          <v-col cols="12" md="6" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardExpiry }}
            </div></v-col
          >
          <v-col cols="12" md="6" class="py-0"
            ><div class="text-error v-messages">
              {{ cardErrors.cardCvc }}
            </div></v-col
          >
        </v-row>
      </div>
    </div>
    <v-form ref="form" id="order-form" validate-on="blur">
      <h3 class="mt-4">Billing Address</h3>
      <div class="form-group">
        <v-switch
          :label="
            form.billingSameAsDelivery
              ? 'Toggle to use different billing address'
              : 'Toggle to use same as delivery address'
          "
          v-model="form.billingSameAsDelivery"
          @update:model-value="resetBillingAddress"
          hide-details
        >
        </v-switch>
        <transition name="fade">
          <!-- STRIPE ADDRESS ELEMENT -->
          <div v-show="!form.billingSameAsDelivery" ref="billing-address-element"></div>
        </transition>
        <!-- error display -->
        <p v-if="billingAddressError" class="text-red">
          {{ billingAddressError }}
        </p>
      </div>
      <h3 class="mt-4">Sign Off</h3>
      <div class="form-group">
        <p class="py-4 ml-8 mb-0">
          I confirm the following highlighted features of my order. I also acknowledge that any changes required after
          placing my order may incur additional costs and/or delay my order.
        </p>
        <p class="ml-8 pb-4 mb-0">
          <small><i>This is just an overview and I have thoroughly checked the full details of my order.</i></small>
        </p>
        <!-- visual sign off -->
        <v-checkbox
          class="sign-off"
          label="Confirm Visual is correct (including handle positions)"
          v-model="form.visualApproval"
          color="accent"
          :rules="[$rules.checked('visual sign off')]"
        ></v-checkbox>
        <!-- fronts sign off -->

        <template v-if="version.fronts && version.fronts.length > 0">
          <v-checkbox
            class="sign-off"
            v-for="material in frontSignOff"
            :name="material.id"
            :key="material.id"
            :label="`Fronts, Cover Panels and/or Plinths in ${material.colourCode ? material.colourCode : ''} ${
              material.label
            } ${material.thickness}mm ${material.unfinished ? '(unfinished)' : ''}`"
            v-model="form.frontsApproval"
            color="accent"
            :rules="[$rules.checked('fronts sign off')]"
          ></v-checkbox>
        </template>
        <!-- handles sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.handles && version.handles.length > 0"
          :label="`${handlesSignOff} Handles`"
          v-model="form.handlesApproval"
          color="accent"
          :rules="[$rules.checked('handles sign off')]"
        ></v-checkbox>
        <!-- spacers sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.spacerPanels && version.spacerPanels.length > 0"
          :label="`${spacerSignOff} Spacer Panels`"
          v-model="form.spacersApproval"
          color="accent"
          :rules="[$rules.checked('spacers sign off')]"
        ></v-checkbox>
        <!-- worktops sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.worktops && version.worktops.length > 0"
          :label="`${worktopSignOff.sentence} Worktops ${worktopSignOff.features}`"
          v-model="form.worktopsApproval"
          color="accent"
          :rules="[$rules.checked('worktops sign off')]"
        ></v-checkbox>
        <!-- urtil sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.urtils && version.urtils.length > 0"
          :label="`${urtilSignOff.sentence} Urtils ${urtilSignOff.features}`"
          v-model="form.urtilsApproval"
          color="accent"
          :rules="[$rules.checked('urtil sign off')]"
        ></v-checkbox>
        <!-- pantry sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.pantries && version.pantries.length > 0"
          :label="`${pantrySignOff.sentence} Pantries ${pantrySignOff.features}`"
          v-model="form.pantriesApproval"
          color="accent"
          :rules="[$rules.checked('pantry sign off')]"
        ></v-checkbox>
        <!-- additional items sign off -->
        <v-checkbox
          class="sign-off"
          v-if="version.additionalItems && version.additionalItems.length > 0"
          label="Confirm Additional Items"
          v-model="form.additionalItemsApproval"
          color="accent"
          :rules="[$rules.checked('additional item sign off')]"
        ></v-checkbox>
      </div>
    </v-form>
    <div class="mt-5" style="display: flex; justify-content: space-between">
      <v-btn color="primary" variant="text" class="mx-0 px-0" @click="$emit('back')"
        ><v-icon>mdi-chevron-left</v-icon>Back to information</v-btn
      >
      <v-btn color="accent" variant="flat" class="mx-0" @click="submit">{{
        form.bankTransfer ? 'Continue' : 'Pay'
      }}</v-btn>
    </div>
  </div>
</template>

<script>
import productData from '@/js/productData'
import { validateUkPostcode, validateEuPostcode } from '@/js/validators'
import { resolveWorktop, resolveWorktopColour } from '@/js/WorktopResolver'

import uid from 'uid'
import monodefault from 'payment-icons/min/mono/default.svg'
import security from 'payment-icons/min/mono/security-code.svg'
import visa from 'payment-icons/min/single/visa.svg'
import mastercard from 'payment-icons/min/flat/mastercard.svg'
import amex from 'payment-icons/min/flat/amex.svg'
import discover from 'payment-icons/min/flat/discover.svg'
import diners from 'payment-icons/min/flat/diners.svg'
import jcb from 'payment-icons/min/flat/jcb.svg'
import unionpay from 'payment-icons/min/flat/unionpay.svg'

export default {
  name: 'PaymentEU',
  props: ['form', 'elements', 'stripeObjects', 'region', 'version'],
  data() {
    return {
      productData: productData,
      billingAddressError: null,
      cardErrors: {},
      cardIcons: {
        visa: visa,
        mastercard: mastercard,
        amex: amex,
        discover: discover,
        diners: diners,
        jcb: jcb,
        unionpay: unionpay,
        unknown: monodefault,
        security: security
      },
      uid: uid,
      stripeElementBaseStyle: {
        base: {
          fontSize: '16px',
          fontFamily: 'proxima-nova, sans-serif',
          fontSmoothing: 'antialiased',
          letterSpacing: '0.5px'
        }
      },
      cardNumberFocussed: false,
      cardExpiryFocussed: false,
      cardCvcFocussed: false,
      cardType: 'unknown',
      billingAddressDetails: null,
      googleMapsApiRetrieved: false
    }
  },
  mounted() {
    this.mountBillingAddress()
    this.mountStripe()
  },
  beforeUnmount() {
    this.unmountStripe()
    this.unmountBillingAddress()
  },
  watch: {
    'form.bankTransfer'(val) {
      // QT may flip the order's payment method mid-checkout; mount/unmount accordingly
      if (val) {
        this.unmountStripe()
      } else {
        this.mountStripe()
      }
    }
  },
  computed: {
    cardComplete() {
      return (
        Object.keys(this.cardErrors).length == 0 &&
        this.stripeObjects.cardNumber &&
        this.stripeObjects.cardNumber._complete &&
        this.stripeObjects.cardExpiry &&
        this.stripeObjects.cardExpiry._complete &&
        this.stripeObjects.cardCvc &&
        this.stripeObjects.cardCvc._complete
      )
    },
    frontSignOff() {
      let materialRequired = this.version.materials.filter((material) => {
        if (
          this.version.pricing?.fronts[material.id] &&
          this.version.pricing.fronts[material.id].total > 0 &&
          [24, 25, 36, 18, 4].includes(material.thickness)
        ) {
          return material
        }
      })
      return materialRequired
    },
    handlesSignOff() {
      let handlesList = []

      this.version.handles.forEach((handle) => {
        let baseName = handle.label.includes(' - ') ? handle.label.split(' - ')[1] : handle.label
        let handleName = baseName.includes(' / ') ? handle.label.split(' - ')[1].split(' / ')[0] : baseName
        let handleMaterial = baseName.includes(' / ') ? handle.label.split(' - ')[1].split(' / ')[1] : ''
        let handleFullName = handleName == 'None' ? 'None Handle' : handleMaterial + ' ' + handleName

        if (!handlesList.includes(handleFullName)) handlesList.push(handleFullName)
      })

      let handlesSignOff = this.formSentenceFromArray(handlesList)
      return handlesSignOff
    },
    spacerSignOff() {
      let spacerMaterials = []
      this.version.spacerPanels.forEach((spacer) => {
        let materialName = spacer.material.replace(spacer.material[0], spacer.material[0].toUpperCase())
        if (!spacerMaterials.includes(materialName)) {
          spacerMaterials.push(materialName)
        }
      })
      let spacerSentence = this.formSentenceFromArray(spacerMaterials)
      return spacerSentence
    },
    worktopSignOff() {
      let worktopNameList = []
      let features = {
        dogleg: false,
        upstand: false
      }

      if (this.version.worktops.length > 0) {
        this.version.worktops.forEach((worktop) => {
          const worktopMaterial = resolveWorktop(worktop, this.region)
          const colour = resolveWorktopColour(worktop, this.region)
          const matValue = worktop.colourType || (worktopMaterial && worktopMaterial.material)

          const worktopMaterialName = matValue ? matValue.replace(matValue[0], matValue[0].toUpperCase()) : ''
          const colourText = (colour && colour.name) || worktop.colourCode || ''
          const worktopSentence = (colourText + ' ' + worktopMaterialName).trim()

          if (worktopSentence && !worktopNameList.includes(worktopSentence)) worktopNameList.push(worktopSentence)
          if (worktop.sections && worktop.sections.find((section) => section.dogleg)) features.dogleg = true
          if (worktop.sections && worktop.sections.find((section) => section.customCoreThickness))
            features.upstand = true
        })
      }
      let worktopSentence = this.formSentenceFromArray(worktopNameList)

      let featuresSentence = ''
      if (features.dogleg && !features.upstand) {
        featuresSentence = ' (includes L-shaped cutout)'
      } else if (!features.dogleg && features.upstand) {
        featuresSentence = ' (includes upstand)'
      } else if (features.dogleg && features.upstand) {
        featuresSentence = ' (includes L-shaped cutout & upstand)'
      }

      let worktopDetails = {
        sentence: worktopSentence,
        features: featuresSentence
      }
      return worktopDetails
    },
    urtilSignOff() {
      let urtilDoorList = []
      let urtilCarcassList = []

      this.version.urtils.forEach((urtil) => {
        let urtilType = productData.getUrtilType(urtil.type).label
        let carcassName = this.getMaterialName(urtil.carcass).split('/')[0]

        if (!urtilDoorList.includes(urtilType)) urtilDoorList.push(urtilType)
        if (!urtilCarcassList.includes(carcassName)) urtilCarcassList.push(carcassName)
      })

      let doorSentence = ' (consists of ' + this.formSentenceFromArray(urtilDoorList) + ' Urtils)'
      let carcassSentence = this.formSentenceFromArray(urtilCarcassList)

      let urtilSignOff = {
        sentence: carcassSentence,
        features: doorSentence
      }

      return urtilSignOff
    },
    pantrySignOff() {
      let includesBase = false
      let pantryCarcassList = []

      this.version.pantries.forEach((pantry) => {
        let carcassName = this.getMaterialName(pantry.carcass).split('/')[0]

        if (pantry.carcass != pantry.base && pantry.base) includesBase = true
        if (!pantryCarcassList.includes(carcassName)) pantryCarcassList.push(carcassName)
      })

      let baseSentence = includesBase ? ' (includes laminate base)' : ''
      let carcassSentence = this.formSentenceFromArray(pantryCarcassList)

      let pantrySignOff = {
        sentence: carcassSentence,
        features: baseSentence
      }

      return pantrySignOff
    }
  },
  methods: {
    async mountStripe() {
      await this.$nextTick()
      // Card form is hidden when BACS — nothing to mount
      if (this.form.bankTransfer) return
      if (!this.$refs.cardNumber) return

      this.setupStripeElement('cardNumber', 'cardNumberFocussed')
      this.setupStripeElement('cardExpiry', 'cardExpiryFocussed')
      this.setupStripeElement('cardCvc', 'cardCvcFocussed')
    },
    unmountStripe() {
      this.unmountStripeElement('cardNumber')
      this.unmountStripeElement('cardExpiry')
      this.unmountStripeElement('cardCvc')
    },
    setupStripeElement(el, focus) {
      if (this.stripeObjects[el]) {
        try {
          this.stripeObjects[el].destroy()
        } catch (e) {
          /* ignore */
        }
        this.stripeObjects[el] = null
      }
      this.stripeObjects[el] = this.elements.create(el, {
        style: this.stripeElementBaseStyle,
        placeholder: ''
      })
      this.stripeObjects[el].mount(this.$refs[el])

      this.stripeObjects[el].addEventListener('change', (event) => {
        if (event.brand) this.cardType = event.brand

        if (event.error) {
          if (!this.cardErrors[event.elementType]) {
            this.cardErrors[event.elementType] = event.error.message
          } else {
            this.cardErrors[event.elementType] = event.error.message
          }
        } else if (this.cardErrors[event.elementType]) {
          delete this.cardErrors[event.elementType]
        }
      })
      this.stripeObjects[el].addEventListener('focus', () => {
        this[focus] = true
      })
      this.stripeObjects[el].addEventListener('blur', () => {
        this[focus] = !this.stripeObjects[el]._empty
      })
    },
    unmountStripeElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
        this.stripeObjects[el].removeEventListener('change')
        this.stripeObjects[el].removeEventListener('focus')
        this.stripeObjects[el].removeEventListener('blur')
      } catch (e) {
        /* ignore */
      }
      this.stripeObjects[el] = null
    },
    resetBillingAddress() {
      if (this.form.billingSameAsDelivery) {
        this.billingAddressDetails = null
        this.unmountBillingAddress()
      } else {
        let billingElement = this.elements.getElement('address')
        if (!billingElement) {
          this.mountBillingAddress()
        }
      }
    },
    mountBillingAddress() {
      this.setupBillingAddressElement('billing-address-element')
    },
    unmountBillingAddress() {
      this.unmountBillingAddressElement('billing-address-element')
    },
    setupBillingAddressElement(el) {
      let placesKey
      if (window.location.hostname == 'mega.plykea.com') {
        placesKey = 'AIzaSyAv4aRIjITY5teVksdZiTZkJiYW2-AtkjU' //prod
      } else {
        placesKey = 'AIzaSyCIUq0Y4cXiT9oIsPN4JReKvOS1C5QQf58' //dev
      }
      let addressOptions = {
        mode: 'billing',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: placesKey
        },
        blockPoBox: true,
        display: {
          name: 'split'
        }
      }
      const stale = this.elements.getElement('address')
      if (stale) {
        try {
          stale.destroy()
        } catch (e) {
          /* already destroyed */
        }
      }
      this.stripeObjects[el] = this.elements.create('address', addressOptions)
      this.stripeObjects[el].mount(this.$refs[el])
    },
    unmountBillingAddressElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
      } catch (e) {
        /* already destroyed */
      }
      this.stripeObjects[el] = null
    },
    async submit() {
      try {
        if (!this.form.billingSameAsDelivery) {
          const billingElement = this.elements.getElement('address')
          const billingResult = await billingElement.getValue()

          if (
            billingResult.complete &&
            billingResult.value.address.postal_code &&
            billingResult.value.address.line1 &&
            billingResult.value.address.city
          ) {
            this.billingAddressError = null // Clear error if all required fields are filled
            this.billingAddressDetails = billingResult.value
          } else {
            this.billingAddressError = 'Please complete all required billing address fields'
            return
          }

          // Validate postcode explicitly here:
          if (this.region === 'uk') {
            const isPostcodeValid = await validateUkPostcode(this.billingAddressDetails.address.postal_code)
            if (!isPostcodeValid) {
              this.billingAddressError = 'Invalid UK postcode'
              return
            } else {
              this.billingAddressError = null // Clear error if postcode is valid
            }
          } else {
            const country = this.billingAddressDetails.address.country
            const pc = this.billingAddressDetails.address.postal_code || ''
            if (!validateEuPostcode(pc, country)) {
              this.billingAddressError = 'Invalid postcode'
              return
            }
            this.billingAddressError = null
          }

          // Run other validations, the sign-off and card details
          const isValid = await this.$utils.validateForm(this.$refs.form)
          if (isValid && (this.cardComplete || this.form.bankTransfer)) {
            this.$emit('success', this.form, this.billingAddressDetails)
          } else {
            return
          }
        } else {
          // Just validate the sign-off and card details
          const isValid = await this.$utils.validateForm(this.$refs.form)
          if (isValid && (this.cardComplete || this.form.bankTransfer)) {
            // billing address will be set as delivery address at Checkout.vue
            this.$emit('success', this.form)
          }
        }
      } catch (error) {
        console.error('Submit error:', error)
        return
      }
    },
    formSentenceFromArray(array) {
      let connectGrammar = array.length == 2 ? ' and ' : ', and '

      let formedSentence =
        array.length == 1 ? array[0] : array.slice(0, array.length - 1).join(', ') + connectGrammar + array.slice(-1)

      return formedSentence
    },
    getMaterialName(code) {
      for (var i = 0; i < this.version.materials.length; i++) {
        if (code == this.version.materials[i].id) {
          var m = this.version.materials[i]
          return m.colourCode ? m.label + ' / ' + m.colourCode : m.label
        }
      }
      return ''
    }
  }
}
</script>

<style lang="scss">
#payment {
  .stripe-input {
    position: relative;
    margin-top: 16px;
    border-bottom: solid 1px #949494;
    .fancy-label {
      font-size: 16px;
      position: absolute;
      top: 5px;
      opacity: 0.7;
      transition: transform 0.25s, opacity 0.5s;
      transform-origin: top left;
      &.tiny {
        opacity: 1;
        transform: scale(0.7) translate(0, -25px);
      }
    }
  }
  .sign-off {
    .v-input__slot {
      align-items: flex-start;
    }
  }
  .StripeElement {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .v-switch {
    margin: 10px 0;
  }
  .v-checkbox {
    margin-top: 5px;
  }
  .v-btn-toggle {
    button {
      flex: 1;
    }
  }
}
</style>
